import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownDescriptionProps {
  content: string
  /** Collapsed pixel height before "Show more" appears. Default 200. */
  collapsedHeight?: number
  className?: string
}

/**
 * Renders a markdown description (including images and links) with an
 * expand/collapse toggle when the content overflows the collapsed height.
 * Uses theme-aware Tailwind classes so it works in light and dark mode.
 */
export default function MarkdownDescription({
  content,
  collapsedHeight = 200,
  className,
}: MarkdownDescriptionProps) {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    // Re-measured on every size change: a one-off check at mount misses fonts, images and width
    // changes that land later, and the toggle then never appears.
    const check = () => setOverflows(el.scrollHeight > collapsedHeight + 8)
    check()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [content, collapsedHeight])

  return (
    <div className={className}>
      <div
        // Collapsed, the text scrolls inside the box, so a long description is readable even
        // before "Show more" is pressed.
        className={expanded ? '' : 'overflow-y-auto'}
        style={expanded ? undefined : { maxHeight: `${collapsedHeight}px` }}
      >
        <div ref={contentRef} className="text-theme-text-secondary text-sm leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                />
              ),
              img: ({ node, ...props }) => (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img
                  {...props}
                  loading="lazy"
                  className="my-2 max-w-full h-auto rounded border border-theme-border-primary"
                />
              ),
              h1: ({ node, ...props }) => <h1 {...props} className="text-xl font-semibold mt-4 mb-2 text-theme-text-primary" />,
              h2: ({ node, ...props }) => <h2 {...props} className="text-lg font-semibold mt-4 mb-2 text-theme-text-primary" />,
              h3: ({ node, ...props }) => <h3 {...props} className="text-base font-semibold mt-3 mb-1 text-theme-text-primary" />,
              p: ({ node, ...props }) => <p {...props} className="my-2" />,
              ul: ({ node, ...props }) => <ul {...props} className="list-disc list-inside my-2 space-y-1" />,
              ol: ({ node, ...props }) => <ol {...props} className="list-decimal list-inside my-2 space-y-1" />,
              code: ({ node, className, children, ...props }) => {
                const isBlock = className?.includes('language-')
                if (isBlock) {
                  return (
                    <pre className="bg-theme-secondary border border-theme-border-primary rounded p-3 overflow-x-auto my-2">
                      <code className={className} {...props}>{children}</code>
                    </pre>
                  )
                }
                return <code className="bg-theme-secondary px-1 py-0.5 rounded text-xs" {...props}>{children}</code>
              },
              blockquote: ({ node, ...props }) => (
                <blockquote {...props} className="border-l-2 border-theme-border-primary pl-3 my-2 text-theme-text-tertiary" />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
